import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  DocumentService,
  DocumentSummary,
  PartyType,
  ProductType,
  TimesheetDetail,
  TimesheetService,
  TimesheetTypeService,
  TodoSummary
} from '../../../../../api';
import {injectMutation, injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {QueryKey} from '../../../../constants/query-key';
import {TranslatePipe} from '@ngx-translate/core';
import {ElciaDatePicker} from '../../../../components/date-picker/date-picker';
import {ElciaTimePicker} from '../../../../components/time-picker/time-picker';
import {PartyDropdown} from '../../../../components/party-dropdown/party-dropdown';
import {TextInput} from '../../../../components/text-input/text-input';
import {NumericInput} from '../../../../components/numeric-input/numeric-input';
import {TimesheetTypeDropdown} from '../timesheet-type-dropdown/timesheet-type-dropdown';
import {TimesheetStatusDropdown} from '../timesheet-status-dropdown/timesheet-status-dropdown';
import {TimesheetFormModel} from '../../models/timesheet-form-model';
import {TimesheetFormValue} from '../../models/timesheet-form-value';
import {DateService} from '../../../../services/date.service';
import {TextArea} from '../../../../components/text-area/text-area';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {TodoPicker} from '../../../todos/components/todo-picker/todo-picker';
import {Divider} from 'primeng/divider';
import {DocumentUploader} from '../../../../components/document-uploader/document-uploader';
import {CanAccessFinances} from '../../../../directives/can-access-finances';
import {Label} from '../../../../components/label/label';
import {ToggleSwitch} from '../../../../components/toggle-switch/toggle-switch';
import {ProductDropdown} from '../../../products/components/product-dropdown/product-dropdown';
import {ActivityTypeDropdown} from '../activity-type-dropdown/activity-type-dropdown';
import {CurrentUserService} from '../../../../services/current-user.service';

@Component({
  selector: 'elcia-timesheet-form',
  imports: [TranslatePipe, ElciaDatePicker, ElciaTimePicker, PartyDropdown, TextInput, NumericInput, TimesheetTypeDropdown, TimesheetStatusDropdown, TextArea, ElciaButton, TodoPicker, Divider, DocumentUploader, CanAccessFinances, Label, ToggleSwitch, ProductDropdown, ActivityTypeDropdown],
  templateUrl: './timesheet-form.html',
  styleUrl: './timesheet-form.scss',
})
export class TimesheetForm {
  private dateService = inject(DateService);
  protected readonly currentUserService = inject(CurrentUserService);
  private readonly documentService = inject(DocumentService);
  private readonly timesheetService = inject(TimesheetService);
  private readonly timesheetTypeService = inject(TimesheetTypeService);

  partyTypes = signal<PartyType[]>([PartyType.Company, PartyType.Contact]);
  protected readonly ProductType = ProductType;
  initialValue = input<TimesheetDetail>();
  documents = input<DocumentSummary[]>([]);
  onSubmit = output<TimesheetFormValue>();

  pendingFiles = signal<File[]>([]);
  pendingDeletionIds = signal<number[]>([]);

  protected displayedDocuments = computed(() =>
    this.documents().filter(d => !this.pendingDeletionIds().includes(d.id))
  );

  protected timesheetForm = new FormGroup<TimesheetFormModel>({
    subject: new FormControl(null),
    concernedPartyId: new FormControl(null),
    date: new FormControl(new Date(), {nonNullable: false, validators: [Validators.required]}),
    startTime: new FormControl(null, Validators.required),
    endTime: new FormControl(null, Validators.required),
    durationInMinutes: new FormControl(null),
    isBillable: new FormControl(false, {nonNullable: true}),
    billableDurationInMinutes: new FormControl(null),
    description: new FormControl(null),
    billingDescription: new FormControl(null),
    timesheetTypeId: new FormControl(null, Validators.required),
    statusId: new FormControl(null),
    todoId: new FormControl(null),
    mileage: new FormControl(null),
    travelExpensesAmount: new FormControl(null),
    productIds: new FormControl<number[]>([], {nonNullable: true}),
    activityTypeIds: new FormControl<number[]>([], {nonNullable: true})
  });

  private readonly typesQuery = injectQuery(() => ({
    queryKey: [QueryKey.TIMESHEET_TYPES_SELECT],
    queryFn: () => lastValueFrom(this.timesheetTypeService.getTimesheetTypeSelect()),
  }));

  private readonly selectedTypeId = toSignal(
    this.timesheetForm.controls.timesheetTypeId.valueChanges,
    {initialValue: this.timesheetForm.controls.timesheetTypeId.value},
  );

  private readonly isBillable = toSignal(
    this.timesheetForm.controls.isBillable.valueChanges,
    {initialValue: this.timesheetForm.controls.isBillable.value},
  );

  protected readonly hasTravelExpenses = computed(() => {
    const id = this.selectedTypeId();
    if (id == null) return false;
    return this.typesQuery.data()?.timesheetTypes.find(t => t.id === id)?.hasTravelExpenses ?? false;
  });

  // Synchronise les champs facturation avec l'état facturable.
  // - Non facturable : on masque et réinitialise les champs.
  // - Facturable : on pré-remplit la durée facturable avec la durée totale tant que
  //   l'utilisateur ne l'a pas saisie manuellement.
  private readonly syncBillableFields = effect(() => {
    if (!this.isBillable()) {
      this.timesheetForm.controls.billableDurationInMinutes.setValue(null, {emitEvent: false});
      this.timesheetForm.controls.billingDescription.setValue(null, {emitEvent: false});
      this.billableDurationManuallySet = false;
      return;
    }
    if (!this.billableDurationManuallySet) {
      this.timesheetForm.controls.billableDurationInMinutes.setValue(
        this.timesheetForm.controls.durationInMinutes.value,
        {emitEvent: false},
      );
    }
  });

  // A billable timesheet must target a client (mirrors the backend rule): the concerned party
  // becomes required as soon as the timesheet is flagged billable.
  private readonly applyConcernedPartyValidator = effect(() => {
    const control = this.timesheetForm.controls.concernedPartyId;
    if (this.isBillable()) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({emitEvent: false});
  });

  // Réinitialise les champs déplacement masqués quand le type sélectionné n'a pas de frais de déplacement.
  // On attend le chargement des types pour ne pas effacer les valeurs d'une prestation en édition.
  private readonly resetTravelFieldsWhenHidden = effect(() => {
    if (!this.typesQuery.isSuccess()) return;
    if (this.hasTravelExpenses()) return;
    this.timesheetForm.controls.mileage.setValue(null, {emitEvent: false});
    this.timesheetForm.controls.travelExpensesAmount.setValue(null, {emitEvent: false});
  });

  protected readonly travelDistanceMutation = injectMutation(() => ({
    mutationFn: (customerId: number) =>
      lastValueFrom(this.timesheetService.getTravelDistance(customerId)),
    onSuccess: res => {
      this.timesheetForm.controls.mileage.setValue(res.distanceInKm);
    },
  }));

  private readonly concernedPartyId = toSignal(
    this.timesheetForm.controls.concernedPartyId.valueChanges,
    {initialValue: this.timesheetForm.controls.concernedPartyId.value},
  );

  // Re-masque l'itinéraire quand le client change : le trajet ne correspond plus au client sélectionné.
  private readonly resetTravelDistanceWhenPartyChanges = effect(() => {
    this.concernedPartyId();
    this.travelDistanceMutation.reset();
  });

  private isUpdatingFromCalculation = false;
  // Passe à true dès que l'utilisateur saisit lui-même la durée facturable :
  // on cesse alors de la recalculer automatiquement depuis la durée totale.
  private billableDurationManuallySet = false;

  constructor() {
    this.timesheetForm.controls.startTime.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.calculateDuration());
    this.timesheetForm.controls.endTime.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.calculateDuration());
    this.timesheetForm.controls.durationInMinutes.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.calculateEndTime());
    // Les recalculs internes utilisent emitEvent:false : seul une saisie utilisateur déclenche cet abonnement.
    this.timesheetForm.controls.billableDurationInMinutes.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.billableDurationManuallySet = true);
  }

  private calculateDuration(): void {
    if (this.isUpdatingFromCalculation) return;

    const startTime = this.timesheetForm.controls.startTime.value;
    const endTime = this.timesheetForm.controls.endTime.value;

    if (startTime && endTime) {
      const durationMinutes = this.dateService.getMinutesDifference(startTime, endTime);
      this.isUpdatingFromCalculation = true;
      this.timesheetForm.controls.durationInMinutes.setValue(durationMinutes, {emitEvent: false});
      this.syncBillableDuration(durationMinutes);
      this.isUpdatingFromCalculation = false;
    }
  }

  private calculateEndTime(): void {
    if (this.isUpdatingFromCalculation) return;

    const startTime = this.timesheetForm.controls.startTime.value;
    const duration = this.timesheetForm.controls.durationInMinutes.value;

    if (startTime && duration !== null && duration !== undefined) {
      const endTime = this.dateService.addMinutesToTime(startTime, duration);
      this.isUpdatingFromCalculation = true;
      this.timesheetForm.controls.endTime.setValue(endTime, {emitEvent: false});
      this.syncBillableDuration(duration);
      this.isUpdatingFromCalculation = false;
    }
  }

  // Aligne la durée facturable sur la durée totale tant que l'utilisateur ne l'a pas modifiée lui-même.
  private syncBillableDuration(durationMinutes: number | null): void {
    if (this.billableDurationManuallySet) return;
    this.timesheetForm.controls.billableDurationInMinutes.setValue(durationMinutes, {emitEvent: false});
  }

  protected calculateDistance(): void {
    const customerId = this.timesheetForm.controls.concernedPartyId.value;
    if (customerId == null) return;
    this.travelDistanceMutation.mutate(customerId);
  }

  protected openItinerary(): void {
    const mapUrl = this.travelDistanceMutation.data()?.mapUrl;
    if (!mapUrl) return;
    window.open(mapUrl, '_blank');
  }

  public submit(): void {
    if (this.timesheetForm.invalid) {
      this.timesheetForm.markAllAsTouched();
      return;
    }
    const formValue = this.timesheetForm.value;
    this.onSubmit.emit({
      id: this.initialValue()?.id ?? null,
      subject: formValue.subject ?? null,
      concernedPartyId: formValue.concernedPartyId ?? null,
      date: formValue.date!,
      startTime: formValue.startTime ?? null,
      endTime: formValue.endTime ?? null,
      isBillable: formValue.isBillable ?? false,
      billableDurationInMinutes: formValue.billableDurationInMinutes ?? null,
      description: formValue.description ?? null,
      billingDescription: formValue.billingDescription ?? null,
      timesheetTypeId: formValue.timesheetTypeId ?? null,
      statusId: formValue.statusId ?? null,
      todoId: formValue.todoId ?? null,
      mileage: this.hasTravelExpenses() ? (formValue.mileage ?? null) : null,
      travelExpensesAmount: this.hasTravelExpenses() ? (formValue.travelExpensesAmount ?? null) : null,
      productIds: formValue.productIds ?? [],
      activityTypeIds: formValue.activityTypeIds ?? []
    });
  }

  isTodoPickerDisplayed = signal(false);

  protected togglePicker() {
    this.isTodoPickerDisplayed.update(v => !v);
  }

  protected selectTodo(summary: TodoSummary | null) {
    // Picking a todo pre-fills the concerned party from the todo's own concern,
    // but only when the timesheet has none yet — never overwrite a user's choice.
    const concernControl = this.timesheetForm.controls.concernedPartyId;
    if (summary?.concern?.id != null && concernControl.value == null) {
      concernControl.setValue(summary.concern.id);
    }
  }

  protected addPendingFiles(files: File[]): void {
    this.pendingFiles.update(current => [...current, ...files]);
  }

  protected removePendingFile(index: number): void {
    this.pendingFiles.update(current => current.filter((_, i) => i !== index));
  }

  protected markForDeletion(doc: DocumentSummary): void {
    this.pendingDeletionIds.update(ids => [...ids, doc.id]);
  }

  protected downloadDocument(doc: DocumentSummary): void {
    this.documentService.downloadDocument(doc.id).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  resetPendingDocuments(): void {
    this.pendingFiles.set([]);
    this.pendingDeletionIds.set([]);
  }

  onInitialValueChange = effect(() => {
    const val = this.initialValue();
    this.pendingFiles.set([]);
    this.pendingDeletionIds.set([]);
    this.billableDurationManuallySet = false;
    if (!val) return;
    if (val.todo?.id) {
      this.isTodoPickerDisplayed.set(true);
    }
    this.isUpdatingFromCalculation = true;
    this.timesheetForm.patchValue({
      subject: val.subject ?? null,
      concernedPartyId: val.concern?.id ?? null,
      date: val.date,
      startTime: val.startTime,
      endTime: val.endTime,
      description: val.description ?? null,
      billingDescription: val.billingDescription ?? null,
      timesheetTypeId: val.timesheetType?.id ?? null,
      statusId: val.status?.id ?? null,
      durationInMinutes: val.durationInSeconds ? val.durationInSeconds / 60 : null,
      isBillable: val.isBillable,
      billableDurationInMinutes: val.billableDurationInSeconds ? val.billableDurationInSeconds / 60 : null,
      todoId: val.todo?.id,
      mileage: val.mileage ?? null,
      travelExpensesAmount: val.travelExpensesAmount ?? null,
      productIds: [],
      activityTypeIds: val.activityTypes?.map(a => a.id) ?? [],
    });
    this.isUpdatingFromCalculation = false;
  });
}
